import React, { FormEvent, FunctionComponent, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useBillingStore } from '../../../stores/billing/useBillingStore'
import Column from '../../../components/Column'
import BoxContainer from '../../../fragments/BoxContainer'
import Text from '../../../components/Text'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import ActionText from '../../../components/ActionText'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { AppRoutes } from '../../../interfaces/store/appStore'
import Row from '../../../components/Row'
import { styles } from '../../../styles/themes/style'
import SvgFragment from '../../../fragments/SvgFragment'
import { IFormProperty } from '../../../interfaces/common'
import FormControl from '../../../components/FormControl'
import { useBillingHandlers } from '../../../services/actions/billing/useBillingHandlers'
import { formatCurrency, formatPrice } from '../../../utils'
import DiscountFormBox from '../components/DiscountFormBox'
import CurrencySelect from '../components/CurrencySelect'
import Button from '../../../components/Button'
import CycleToggle from '../components/CycleToggle'
import { IBillingPlanV2, IPrice } from '../../../interfaces/models/billingPlan'
import InformationBox from '../../../components/InformationBox'
import Select from '../../../components/select/Select'
import CompareFeatures from './CompareFeatures/CompareFeatures'
import { IUpcomingPayment } from '../../../interfaces/response/upcomingPayment'
import { canOnlyBuyGrowth, getPlanDisplayDescription, getPlanDisplayName } from '../v2/pricingV2'

interface IModifyPlanSectionProps { }

interface IPaymentFormState extends Record<string, any> {
    quantity: IFormProperty
}

interface ITotalsFormState extends Record<string, any> {
    discountAmount: IFormProperty,
    subtotal: IFormProperty,
    total: IFormProperty
    stripeCredits: IFormProperty
}

const ModifyPlanSection: FunctionComponent<IModifyPlanSectionProps> = () => {
    const location = useLocation()
    let { changeSeats, changeCycle, preSelectedPrice, preSelectedBillingPlan, preSelectedCoupon, currentSeats, yearlyOnly } = location.state || {}
    const { store: { selectedPrice, selectedBillingPlan, upcomingPayment, appliedCoupon, billingPlans } } = useBillingStore()
    const { setSelectedPlanAndPriceHandler, getSetupIntentHandler, calculateUpcomingPaymentHandler, applyCouponHandler } = useBillingHandlers()
    const { store: { workspace, subscription, trial, lifetime } } = useAuthStore()
    const navigate = useNavigate()
    const [isCalculatingUpcomingPayment, setIsCalculatingUpcomingPayment] = useState(false);
    const [upcomingPaymentWithoutCoupon, setUpcomingPaymentWithoutCoupon] = useState<IUpcomingPayment | undefined>();

    useEffect(() => {
        if(preSelectedBillingPlan && preSelectedPrice) setSelectedPlanAndPriceHandler(preSelectedBillingPlan, preSelectedPrice.interval, preSelectedPrice.currency)
        if(preSelectedCoupon) applyCouponHandler(preSelectedCoupon)
    }, 
    [preSelectedPrice, preSelectedBillingPlan])

    const fallbackSeats = 1
    const seatsUsed = lifetime && workspace?.seatsUsed ? workspace?.seatsUsed - 1 : workspace?.seatsUsed ?? fallbackSeats;
    // When user has an active subscription, use purchased seats (subscription.quantity) as the base
    // Otherwise, use seatsUsed for trial or new subscriptions
    const hasActiveSubscription = subscription?.isValid && !trial && !(subscription?.cancelDate && subscription?.isValid);
    const effectiveSeats = hasActiveSubscription && subscription?.quantity ? subscription.quantity : seatsUsed;
    // Minimum is the number of actual users, so purchased seats can be reduced down to it
    const minNumberOfSeats = Math.max(fallbackSeats, seatsUsed ?? fallbackSeats)
    const defaultQuantity = Math.max(trial ? fallbackSeats
        : effectiveSeats ?? fallbackSeats, minNumberOfSeats) ?? fallbackSeats;

    let userDefinedPrice: IPrice | undefined
    const selectedUserBillingPlan = billingPlans.find((plan: IBillingPlanV2) => {
        userDefinedPrice = plan.prices.find((price: IPrice) => price.priceId === subscription?.priceId && price.productId === subscription?.productId)
        if(userDefinedPrice) return true
    })
    const cantSelectSame = subscription?.isValid && !trial && !(subscription?.cancelDate && subscription?.isValid)
    let selectedDefaultQuantity: string
    if(changeSeats && cantSelectSame && userDefinedPrice?.priceId === selectedPrice?.priceId &&  selectedUserBillingPlan?._id === selectedBillingPlan?._id)  selectedDefaultQuantity = ((currentSeats || defaultQuantity) + 1).toString() 
    else if(changeCycle && subscription?.quantity)  selectedDefaultQuantity = subscription.quantity.toString()
    else  selectedDefaultQuantity = defaultQuantity.toString()
    const isAllowedPlan = selectedBillingPlan?.name === 'Starter';
    const getFormOriginalState = (minSeats: number, defaultQuantity: string): IPaymentFormState => ({
        quantity: {
            property: defaultQuantity,
            placeholder: "Enter quantity...",
            errors: [],
            limits: {
                min: minSeats
            },
            async isValid() {
                this.errors.length = 0
                let quantity = +this?.property
                let requiredNumberError = !isNaN(quantity) ? undefined : "Quantity must be a number."
                let requiredMinError = quantity >= (this?.limits?.min ?? 1) ? undefined : `Quantity must be more than or equal to ${(this?.limits?.min ?? 1)}.`
                requiredNumberError && this?.errors?.push(requiredNumberError)
                requiredMinError && this?.errors?.push(requiredMinError)
                updatePaymentErrors('quantity', this?.errors)
                return this?.errors?.length === 0
            }
        }
    });

    const [paymentFormState, setPaymentFormState] = useState<IPaymentFormState>(
        getFormOriginalState(minNumberOfSeats, selectedDefaultQuantity)
    );

    useEffect(() => {
        if (selectedBillingPlan) {
            setPaymentFormState(getFormOriginalState(minNumberOfSeats, selectedDefaultQuantity));
        }
    }, [selectedBillingPlan, minNumberOfSeats, selectedDefaultQuantity]);

    const totalsFormOriginalState: ITotalsFormState = {
        discountAmount: {
            property: "0",
            errors: [],
            async isValid() {
                return this?.errors?.length === 0
            }
        },
        quantity: {
            property: "0",
            errors: [],
            async isValid() {
                return this?.errors?.length === 0
            }
        },
        subtotal: {
            property: "0",
            errors: [],
            async isValid() {
                return this?.errors?.length === 0
            }
        },
        total: {
            property: "0",
            errors: [],
            async isValid() {
                return this?.errors?.length === 0
            }
        },
        totalToPay: {
            property: "0",
            errors: [],
            async isValid() {
                return this?.errors?.length === 0
            }
        },
        stripeCredits: {
            property: "0",
            errors: [],
            async isValid() {
                return this?.errors?.length === 0
            }
        },
    }

    const quantityOptions = Array(100).fill(undefined).map((value: any, index: number) => (
        {label: `${index + 1} seat(s)`, value: `${index + 1}`}
    )).filter((value: any, index: number) => !(upcomingPayment && userDefinedPrice?.priceId === selectedPrice?.priceId &&  selectedUserBillingPlan?._id === selectedBillingPlan?._id && (index+1) === subscription?.quantity))

    const [paymentFormValid, setPaymentFormValid] = useState<boolean>(false)
    const [totalsFormState, setTotalsFormState] = useState<ITotalsFormState>({ ...totalsFormOriginalState })
    const [discountBox, setDiscountBox] = useState<boolean>(false)
    const [showCompareFeatures, setShowCompareFeatures] = useState(false)
    const [buttonState, setButtonState] = useState<string>('')

    const validatePaymentForm = useCallback(async () => {
        setPaymentFormValid(false);
        const validForm = await paymentFormState?.quantity.isValid();
        setPaymentFormValid(validForm);
        return validForm;
    }, [paymentFormState])

    const updatePaymentForm = useCallback((control: any, newValue: string) => {
        setPaymentFormState(previous => {
            let newState = { ...previous, [control]: { ...previous[control], property: newValue.trim() } };
            return newState
        })
    }, [paymentFormState]);

    const updatePaymentErrors = useCallback((control: any, newValue: string[]) => {
        setPaymentFormState(previous => {
            let newState = { ...previous, [control]: { ...previous[control], errors: [...newValue] } };
            return newState
        })
    }, [paymentFormState]);

    const calculateSubtotalAmount = useCallback((quantity: number): number => {
        return selectedBillingPlan?.name === 'Starter' ? (selectedPrice.price / 100) 
            : (selectedPrice.price / 100  + (quantity - fallbackSeats) * ((selectedPrice.price ?? 0) / 100))
    }, [selectedPrice, selectedBillingPlan, fallbackSeats])

    const calculateDiscountAmount = useCallback((subtotal: number): number => {
        if (appliedCoupon?.appliesToProducts?.length <= 0 || appliedCoupon?.appliesToProducts?.includes(selectedPrice?.productId)) {
            if (appliedCoupon?.amountOff) return appliedCoupon?.amountOff / 100
            return appliedCoupon?.percentOff ? (subtotal * appliedCoupon.percentOff) / 100 : 0
        }
        return 0
    }, [appliedCoupon, selectedPrice])

    const updateTotals = useCallback(async () => {
        const quantity = +paymentFormState.quantity.property > minNumberOfSeats ? +paymentFormState.quantity.property : minNumberOfSeats
        let subtotal = quantity ? calculateSubtotalAmount(quantity) : selectedPrice.price / 100;

        let discountAmount = 0;
        let totalToPay = 0;
        let stripeCredits = 0;

        if (upcomingPayment) {
            // Case 1: backend provides an explicit discount (monthly, or when Stripe sends discount_amount > 0)
            if (upcomingPayment.discounts?.length && upcomingPayment.discounts[0].amount > 0) {
                discountAmount = upcomingPayment.discounts[0].amount / 100;
            }
            // Case 2: yearly proration with coupon - Stripe embeds the discount into amountToPay,
            // so FE computes the visual discount as the difference between baseline and with-coupon values
            else if (appliedCoupon && upcomingPaymentWithoutCoupon) {
                const payDifference = upcomingPaymentWithoutCoupon.amountToPay - upcomingPayment.amountToPay;
                const creditDifference = upcomingPayment.amountRemaining - upcomingPaymentWithoutCoupon.amountRemaining;
                const totalSaving = payDifference + creditDifference;

                discountAmount = totalSaving > 0 ? totalSaving / 100 : 0;
            }
            // Total to pay always comes from backend when upcomingPayment exists
            totalToPay = upcomingPayment.amountToPay / 100;
            stripeCredits = upcomingPayment.amountRemaining / 100;
        } else {
            // No upcomingPayment (initial purchase) – use coupon definition
            discountAmount = calculateDiscountAmount(subtotal);
            totalToPay = subtotal - discountAmount < 0 ? 0 : subtotal - discountAmount;
        }

        const total = subtotal - discountAmount < 0 ? 0 : subtotal - discountAmount;
        setTotalsFormState(previous => {
            let newState = { ...previous, 
                            ['quantity']: { ...previous['quantity'], property: quantity.toString() },
                            ['subtotal']: { ...previous['subtotal'], property: subtotal.toString() },
                            ['discountAmount']: { ...previous['discountAmount'], property: discountAmount.toString() },
                            ['total']: { ...previous['total'], property: total.toString() },
                            ['stripeCredits']: { ...previous['stripeCredits'], property: stripeCredits.toString() },
                            ['totalToPay']: { ...previous['totalToPay'], property: totalToPay.toString() }, };
            return newState
        })
    },
    [upcomingPayment, upcomingPaymentWithoutCoupon, paymentFormState, selectedPrice, calculateSubtotalAmount, calculateDiscountAmount, minNumberOfSeats, appliedCoupon])

    useEffect(() => {
        const calculateUpcomingPayment = async () => {
            if (!selectedPrice || trial) return;

            setIsCalculatingUpcomingPayment(true);

            const baseParams = {
                priceId: selectedPrice.priceId,
                quantity: +paymentFormState.quantity.property,
                //currency: selectedPrice.currency //it seems that we can't retrieve this, I will be disabling currency change for active subscriptions
            };

            const isYearly = selectedPrice.interval === 'year';

            if (appliedCoupon && isYearly) {
                // 1) baseline without coupon -> used only for visual discount
                const baseResult = await calculateUpcomingPaymentHandler({
                    ...baseParams,
                    couponId: undefined,
                });
                setUpcomingPaymentWithoutCoupon(baseResult);

                // 2) actual proration with coupon -> this goes into store.upcomingPayment
                await calculateUpcomingPaymentHandler({
                    ...baseParams,
                    couponId: appliedCoupon.id,
                });
            } else {
                // Monthly or no coupon – a single call is enough
                const result = await calculateUpcomingPaymentHandler({
                    ...baseParams,
                    ...(appliedCoupon ? { couponId: appliedCoupon.id } : {}),
                });

                // If coupon is not applied, this is the baseline value
                if (!appliedCoupon) {
                    setUpcomingPaymentWithoutCoupon(result);
                } else {
                    setUpcomingPaymentWithoutCoupon(undefined);
                }
            }

            setIsCalculatingUpcomingPayment(false);
        };

        calculateUpcomingPayment();
        // Intentionally do NOT add calculateUpcomingPaymentHandler to deps to avoid re-creating effect every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentFormState, appliedCoupon, selectedPrice, trial])

    useEffect(() => {
        if(selectedPrice) updateTotals()
    }, [upcomingPayment, paymentFormState, appliedCoupon, selectedPrice, updateTotals])

    const setBillingCycle = useCallback((interval: string) => {
        setSelectedPlanAndPriceHandler(selectedBillingPlan, interval, selectedPrice.currency)
    }, [selectedPrice, selectedBillingPlan])

    const setCurrency = useCallback((currency: string) => {
        setSelectedPlanAndPriceHandler(selectedBillingPlan, selectedPrice.interval, currency)
    }, [selectedPrice, selectedBillingPlan])

    const submitFormHandler = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        setButtonState('isLoading')
        const validForm = await validatePaymentForm();
        if (validForm) {
            const result = await getSetupIntentHandler()
            if (result) navigate(AppRoutes.BILLING_PAYMENT, { state: { setupIntent: result, 
                    selectedPrice: selectedPrice, 
                    selectedBillingPlan: selectedBillingPlan, 
                    quantity: paymentFormState.quantity.property, 
                    appliedCoupon,
                    ...(upcomingPayment?.prorationDate && { prorationDate: upcomingPayment.prorationDate }),
                    totals: {
                        discountAmount: +totalsFormState.discountAmount.property,
                        subtotal: +totalsFormState.subtotal.property,
                        total: +totalsFormState.total.property,
                        totalToPay: +totalsFormState.totalToPay.property,
                        stripeCredits: +totalsFormState.stripeCredits.property
                    },
                    payNow: true } })

        }
        setButtonState('')
    }, [paymentFormState, totalsFormState]);

    // Canceled and trial users can't buy a plan we no longer sell (from Settings, a bookmark, etc.); they pick Growth instead
    if (canOnlyBuyGrowth(subscription, trial, (preSelectedBillingPlan ?? selectedBillingPlan)?.name)) return <Navigate to={AppRoutes.BILLING} replace />

    const selectedPrices = billingPlans.map((plan: any) => {
        const targetInterval = selectedPrice ? selectedPrice?.interval : preSelectedPrice.interval;
        const targetCurrency = selectedPrice ? selectedPrice?.currency : preSelectedPrice.currency;
        
        // Filter for active prices for the target interval and currency
        const filteredPrices = plan.prices.filter((price: any) => {
            if (price?.interval !== targetInterval || price.currency !== targetCurrency) return false;
            
            // Check if this is an active price
            const sameProductIntervalPrices = plan.prices.filter((p: any) => 
                p.interval === price.interval && p.currency === price.currency && p.productId === price.productId
            );
            
            if (sameProductIntervalPrices.length === 1) {
                // If only one price for this product+interval+currency, keep it (backward compatibility)
                return true;
            } else {
                // If multiple prices, only keep active ones
                return price.active === true;
            }
        });
        
        return { name: plan.name, prices: filteredPrices }
    })

    return <Container>
        <StyledColumn>
            <NarrowColumn>
                <Text $heading2 $bold $black>{'Your plan'}</Text>
                <StyledBoxedContainer>
                    <Row alignItems gap="5px">
                        <Text $bold $heading3>{getPlanDisplayName(selectedBillingPlan?.name) ?? ''}</Text>
                        <SmallCheckedIcon><SvgFragment type='checkmark' /></SmallCheckedIcon>
                    </Row>
                    <Text $paragraph1>{getPlanDisplayDescription(selectedBillingPlan?.name, selectedBillingPlan?.description) ?? ''}</Text>
                    <Text $paragraph1 $capitalize>{selectedPrice?.interval ? `${selectedPrice?.interval}ly subscription` : ''}</Text>
                    {/* <Text $paragraph1>{`${ Math.max(subscription?.quantity ?? fallbackSeats, seatsUsed ?? fallbackSeats) ?? fallbackSeats} seat(s)`}</Text> */}
                    {/* {lifetime && <Row gap='3px' alignItems>
                        <Text $paragraph1>1</Text><Tag title={'lifetime'} $borderColor={styles.colors.yellow600} $color={styles.colors.yellow200}/> <Text $paragraph1>license</Text>
                    </Row>} */}
                    {!changeCycle && <>
                        <Divider />
                        <Text $faded={isAllowedPlan} $paragraph1>Select the number of seats</Text>
                        {/* <Text>{`The minimum number of seats is ${ minNumberOfSeats }`}</Text> */}
                        <FormControl $width='150px' $orientation="column">
                        <Select 
                            placeholder={paymentFormState?.quantity?.placeholder}
                            isSearchable={true}
                            options={quantityOptions}
                            onChange={(e:any) => updatePaymentForm('quantity', e.value)}
                            isDisabled={isAllowedPlan}
                            value={{label: `${selectedBillingPlan?.name === 'Starter' ? 1 : paymentFormState?.quantity?.property} seat(s)`, value: paymentFormState?.quantity?.property}}
                        />
                        </FormControl>
                        {+paymentFormState?.quantity?.property < minNumberOfSeats && <InformationBox 
                            message={<StyledText>{`You currently have ${minNumberOfSeats} users on your team. To decrease team size, you will need to remove users from your team. `} <StyledActionText onClickHandler={() => navigate(AppRoutes.SETTINGS_WORKSPACE_ADMIN)}>{`Manage team users.`}</StyledActionText></StyledText>}/>}
                        </>}
                        {(selectedBillingPlan?.name === 'Starter' && workspace?.seatsUsed > 1) && <InformationBox backgroundColorName={'red200'} message={<StyledText>{`You currently have ${workspace?.seatsUsed} users on your team. To decrease team size, you will need to remove users from your team. `} <StyledActionText onClickHandler={() => navigate(AppRoutes.SETTINGS_WORKSPACE_ADMIN)}>{`Manage team users.`}</StyledActionText></StyledText>} />}
                    {!changeSeats && <>
                        <Divider />
                        <CycleToggle prices={!selectedBillingPlan?.prices ? [] : 
                                selectedBillingPlan?.prices?.filter((price: IPrice) => selectedPrice && price.currency === selectedPrice.currency && 
                                ((changeCycle) ? price.interval !== userDefinedPrice?.interval : true))} 
                            selectedInterval={selectedPrice?.interval ?? 'year'}
                            hiddenIntervals={yearlyOnly ? ['month'] : undefined}
                            onClickHandler={({ key }) => setBillingCycle(key)} />
                    </>}
                </StyledBoxedContainer>
            </NarrowColumn>
        </StyledColumn>
        <StyledOutsideBoxedContainer>
            <NarrowColumn>
                <StyledRow alignItems spaceBetween>
                    <Text $heading2 $bold $black $capitalize>{'Subscription summary'}</Text>
                    {!upcomingPayment && !isCalculatingUpcomingPayment && <CurrencySelect prefilledValue={selectedPrice?.currency ?? 'usd'} onChangeHandler={(currency) => setCurrency(currency)} />}
                </StyledRow>
                <StyledInvisibleBox>
                    <Text $heading3>{getPlanDisplayName(selectedBillingPlan?.name) ?? ''}</Text>
                    <StyledRow alignItems spaceBetween>
                        <Row gap="3px" alignItems>
                            <Text $paragraph1 $capitalize>{selectedPrice?.interval ? `${selectedPrice?.interval}ly subscription` : ''}</Text>
                        </Row>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice(selectedPrice?.price ?? 0)}`}</Text>
                    </StyledRow>
                    {selectedPrice?.additionalLicence && totalsFormState?.quantity?.property?.length && totalsFormState?.quantity?.property - fallbackSeats > 0 && <StyledRow alignItems spaceBetween>
                        <Text $paragraph1>{`Additional seat(s) x ${totalsFormState?.quantity?.property - fallbackSeats}`}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice(selectedPrice?.additionalLicence ? (totalsFormState?.quantity?.property - fallbackSeats) * selectedPrice?.additionalLicence : 0)}`}</Text>
                    </StyledRow>}
                    {/* <Text $paragraph1>{`${ Math.max(subscription?.quantity ?? fallbackSeats, seatsUsed ?? fallbackSeats) ?? fallbackSeats} user(s)`}</Text> */}
                    <Divider />
                    <AccordionActionText arrowUp={!discountBox} onClickHandler={() => setDiscountBox(!discountBox)}>{'Do you have a coupon?'}<SvgFragment type={'arrowDown'} /></AccordionActionText>
                    {discountBox && <DiscountFormBox discountAmount={(totalsFormState?.discountAmount?.property ? +totalsFormState?.discountAmount?.property : 0)} currency={selectedPrice?.currency ?? 'usd'} isLoading={isCalculatingUpcomingPayment} />}
                    <StyledRow alignItems spaceBetween>
                        <Text $paragraph1 $capitalize>{'Subtotal'}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice(totalsFormState?.subtotal?.property ? +totalsFormState?.subtotal?.property*100 : 0)}`}</Text>
                    </StyledRow>
                    <Divider />
                    <StyledRow alignItems spaceBetween>
                        <Text $heading5 $capitalize>{`Total Billed ${selectedPrice?.interval}ly`}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice(totalsFormState?.total?.property ? +totalsFormState?.total?.property*100 : 0)}`}</Text>
                    </StyledRow>
                    <Text $lighter $paragraph3>All payments are powered by Stripe.com. Your personal information will never be shared, and all transactions are protected by Stripe.com.</Text>
                    <svg xmlns="http://www.w3.org/2000/svg" width="150" height="34" viewBox="0 0 150 34">
                        <g fill="none" fillRule="evenodd">
                            <g fill="#8C96AD" fillRule="nonzero">
                                <g>
                                    <path d="M146 0H3.73C1.67 0 0 1.67 0 3.73v26.54C0 32.33 1.67 34 3.73 34H146c2.21 0 4-1.79 4-4V4c0-2.21-1.79-4-4-4zm3 30c0 1.657-1.343 3-3 3H3.73c-1.505-.005-2.725-1.225-2.73-2.73V3.73C1.005 2.225 2.225 1.005 3.73 1H146c1.657 0 3 1.343 3 3v26z" transform="translate(-520 -940) translate(520 940)" />
                                    <path d="M17.07 11.24h-4.3V22h1.92v-4.16h2.38c2.4 0 3.9-1.16 3.9-3.3 0-2.14-1.5-3.3-3.9-3.3zm-.1 5h-2.28v-3.3H17c1.38 0 2.11.59 2.11 1.65s-.76 1.6-2.11 1.6l-.03.05zM25.1 14c-1.063-.012-2.082.425-2.806 1.204-.723.779-1.084 1.827-.994 2.886-.128 1.436.566 2.822 1.793 3.58 1.227.757 2.777.757 4.004 0 1.227-.758 1.92-2.144 1.793-3.58.093-1.058-.265-2.107-.988-2.886-.722-.78-1.74-1.217-2.802-1.204zm0 6.67c-1.22 0-2-1-2-2.58s.76-2.58 2-2.58 2 1 2 2.58-.79 2.57-2 2.57v.01zM36.78 19.35L35.37 14.13 33.89 14.13 32.49 19.35 31.07 14.13 29.22 14.13 31.59 22.01 33.15 22.01 34.59 16.85 36.03 22.01 37.59 22.01 39.96 14.13 38.18 14.13zM44 14c-1.049.02-2.043.468-2.752 1.241-.709.773-1.07 1.803-.998 2.849-.085 1.063.283 2.113 1.012 2.89.729.78 1.752 1.215 2.818 1.2 1.487.08 2.859-.797 3.41-2.18L46 19.38c-.24.796-1 1.319-1.83 1.26-.58.014-1.14-.21-1.55-.62-.41-.41-.634-.97-.62-1.55h5.52v-.6C47.54 15.71 46.32 14 44 14zm-1.93 3.13c.145-.95.97-1.646 1.93-1.63.446-.038.887.118 1.21.428.324.31.499.744.48 1.192l-3.62.01zM50.69 15.3v-1.17h-1.8V22h1.8v-4.13c-.032-.54.168-1.068.55-1.45.382-.382.91-.582 1.45-.55.22-.016.44-.016.66 0v-1.8h-.51c-.896-.046-1.736.434-2.15 1.23zM57.48 14c-1.049.02-2.043.468-2.752 1.241-.709.773-1.07 1.803-.998 2.849-.085 1.063.283 2.113 1.012 2.89.729.78 1.752 1.215 2.818 1.2 1.472.063 2.824-.811 3.37-2.18l-1.54-.59c-.24.796-1 1.319-1.83 1.26-.568-.005-1.11-.238-1.504-.646-.395-.407-.61-.957-.596-1.524H61v-.6c0-2.19-1.24-3.9-3.52-3.9zm-1.93 3.13c.149-.942.967-1.632 1.92-1.62.446-.038.887.118 1.21.428.324.31.499.744.48 1.192h-3.61zM67.56 15c-.562-.66-1.394-1.028-2.26-1-2.21 0-3.47 1.85-3.47 4.09 0 2.24 1.26 4.09 3.47 4.09.867.032 1.7-.337 2.26-1V22h1.8V11.24h-1.8V15zm0 3.35c.082.577-.092 1.16-.477 1.599-.384.438-.94.687-1.523.681-1.31 0-2-1-2-2.52s.7-2.52 2-2.52c1.11 0 2 .81 2 2.29v.47zM79.31 14c-.883-.039-1.734.33-2.31 1v-3.76h-1.8V22H77v-.83c.565.661 1.4 1.03 2.27 1 2.2 0 3.46-1.86 3.46-4.09 0-2.23-1.22-4.08-3.42-4.08zM79 20.6c-.583.006-1.139-.243-1.523-.681-.385-.438-.559-1.022-.477-1.599v-.47c0-1.48.84-2.29 2-2.29 1.3 0 2 1 2 2.52s-.75 2.52-2 2.52zM86.93 19.66L85 14.13h-1.9l2.9 7.59-.3.74c-.106.527-.61.876-1.14.79-.2.015-.4.015-.6 0v1.51c.242.036.486.053.73.05 1.288.094 2.46-.749 2.78-2l3.24-8.62h-1.89l-1.89 5.47zM125 12.43c-.797-.005-1.564.308-2.13.87l-.14-.69h-2.39v12.92l2.72-.59v-3.13c.541.453 1.224.7 1.93.7 1.94 0 3.72-1.59 3.72-5.11 0-3.22-1.8-4.97-3.71-4.97zm-.65 7.63c-.483.028-.953-.163-1.28-.52v-4.11c.328-.37.806-.572 1.3-.55 1 0 1.68 1.13 1.68 2.58 0 1.45-.69 2.6-1.7 2.6zM133.73 12.43c-2.62 0-4.21 2.26-4.21 5.11 0 3.37 1.88 5.08 4.56 5.08 1.046.017 2.079-.234 3-.73v-2.25c-.837.422-1.763.635-2.7.62-1.08 0-2-.39-2.14-1.7h5.38v-1c.09-2.87-1.27-5.13-3.89-5.13zm-1.47 4.07c0-1.26.77-1.79 1.45-1.79.68 0 1.4.53 1.4 1.79h-2.85zM113 13.36l-.17-.82h-2.32v9.71h2.68v-6.58c.496-.602 1.313-.833 2.05-.58v-2.55c-.847-.332-1.808.02-2.24.82zM99.46 15.46c0-.44.36-.61.93-.61.945.02 1.87.267 2.7.72v-2.63c-.857-.347-1.775-.52-2.7-.51-2.21 0-3.68 1.18-3.68 3.16 0 3.1 4.14 2.6 4.14 3.93 0 .52-.44.69-1 .69-1.057-.062-2.084-.37-3-.9V22c.944.421 1.966.64 3 .64 2.26 0 3.82-1.15 3.82-3.16-.05-3.36-4.21-2.76-4.21-4.02zM107.28 10.24l-2.65.58v8.93c-.028.76.259 1.499.792 2.042.534.543 1.267.842 2.028.828.657.03 1.312-.097 1.91-.37V20c-.35.15-2.06.66-2.06-1v-4h2.06v-2.34h-2.06l-.02-2.42zM116.25 11.7L118.98 11.13 118.98 8.97 116.25 9.54zM116.25 12.61H118.98V22.25H116.25z" transform="translate(-520 -940) translate(520 940)" />
                                </g>
                            </g>
                        </g>
                    </svg>
                    <Divider />
                    <StyledRow alignItems spaceBetween>
                        <Text $heading4 $capitalize>{`Total to pay`}{upcomingPayment && <Text $paragraph1 $faded>{'(Proration)'}</Text>}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice(totalsFormState?.totalToPay?.property ? +totalsFormState?.totalToPay?.property*100 : 0)}`}</Text>
                    </StyledRow>
                    {totalsFormState?.stripeCredits?.property !== '0' && <StyledRow alignItems spaceBetween>
                        <Text $heading4 $capitalize>{`Stripe credits`}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice(totalsFormState?.stripeCredits?.property ? +totalsFormState?.stripeCredits?.property*100 : 0)}`}</Text>
                    </StyledRow>}
                    <Button $type={'blue'} isLoading={buttonState === 'isLoading'} disabled={+paymentFormState?.quantity?.property < minNumberOfSeats || (selectedBillingPlan?.name === 'Starter' && workspace?.seatsUsed > 1)} onClickHandler={submitFormHandler}>Check out</Button>{/* disabled={!paymentFormValid} */}
                </StyledInvisibleBox>
            </NarrowColumn>
        </StyledOutsideBoxedContainer>
        {showCompareFeatures && <CompareFeatures selectedPrices={selectedPrices} selectedCycle={selectedPrice?.interval ?? 'year'} currency={selectedPrice?.currency ?? 'usd'} setShowCompareFeatures={setShowCompareFeatures} />}
    </Container>
}

const Container = styled.div`
    width: 100%;
    display: flex;
    height: 100%;
    flex-wrap: wrap;
`

const StyledColumn = styled(Column)`
    flex: 1;
    padding: 16px;
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    gap: 32px;
    align-items: center;
    @media only screen and (min-width: 1264px) {
        padding: 60px;
        align-items: flex-end;
    }
`

const NarrowColumn = styled(Column)`
    width: 500px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    @media only screen and (max-width: 598px) {
        width: auto;
        max-width: 500px;
    }
`

const StyledOutsideBoxedContainer = styled(BoxContainer)`
    align-items: flex-start;
    gap: 32px;
    flex: 1;
    padding: 16px;
    align-items: center;
    @media only screen and (min-width: 1264px) {
        padding: 60px;
        align-items: flex-start;
    }
`

const StyledInvisibleBox = styled(Column)`
    width: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    gap: 15px;
    padding: 16px 0px;
`
const StyledBoxedContainer = styled(BoxContainer)`
    align-items: flex-start;
`
const Divider = styled.div`
    width: 100%;
    height: 1px;
    background: ${({ theme: { quinaryColor } }) => `${quinaryColor}`};
    margin: 10px 0;
`
const SmallCheckedIcon = styled.div`
    border-radius: 50%;
    background-color: ${styles.colors.primary600};
    width: 16px;
    height: 16px;
    display: flex;
    margin-right: 10px;
    cursor: pointer;
    align-items: center;

    & .svg .path {
        fill: ${({ theme: { secondaryColor } }) => secondaryColor};
    }
`
const StyledRow = styled(Row)`
    width: 100%;
`

const StyledText =  styled(Text)`
    display: inline-block;
    text-align: left;
`
const StyledActionText = styled(ActionText)`
    display: inline-block;
    margin-left: 3px;
`
const AccordionActionText = styled(ActionText)<{arrowUp: boolean}>`
    display: flex;
    align-items: center;
    .svg {     
        width: 20px;
        height: 20px;
        transition: all .2s ease;
        transform: rotate(180deg);
    }
    .svg .path { fill: ${styles.colors.primary600} }
    &:hover, &:focus {
        .svg .path { fill: ${styles.colors.primary500} }
    }
    ${({ arrowUp }) => arrowUp && `.svg { transform: rotate(360deg); }`}
`

export default ModifyPlanSection
